import { VisibilityOffOutlined as HideIcon } from "@/components/icons";
import * as React from "react";
import { Box, Chip, IconButton, ListItem, Stack, Tooltip, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { DEMO_SESSION_PATH, useDemoSession } from "@/api/demoSession";
import { useHideDemoSession } from "@/features/demo/useHideDemoSession";
import { brand } from "@/theme";

/** The "Demo" chip: the session list entry and the demo's header use the
 *  same one, sized like the list's other chips. No tooltip: a chip isn't
 *  focusable, and the text beside it (the list's "Sample session ·
 *  read-only", the page's banner) already says what it means. */
export function DemoChip(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Chip
      size="small"
      color="secondary"
      variant="outlined"
      label={t("demo_session.chip")}
      data-testid="demo-chip"
      sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
    />
  );
}

/** The demo session's entry at the top of the session list (FW-34): a
 *  card like the others, marked Demo, with a "Hide demo" action instead
 *  of archive and delete. */
export function DemoSessionCard({ selected }: { selected: boolean }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.resolvedLanguage ?? i18n.language;
  const demo = useDemoSession(lang);
  const hide = useHideDemoSession();
  const title = demo.data?.title ?? "…";
  return (
    <ListItem disableGutters sx={{ p: 0, mb: 0.5 }} data-testid="demo-session-card">
      <Box
        sx={{
          width: "100%",
          position: "relative",
          border: 1,
          borderColor: selected ? "primary.main" : "divider",
          borderLeftWidth: 4,
          borderLeftColor: brand.muted,
          borderStyle: "solid",
          borderRadius: 1,
          bgcolor: selected ? "action.selected" : "background.paper",
          overflow: "hidden",
          "&:hover .session-actions, &:focus-within .session-actions": { opacity: 1 },
        }}
      >
        <Box
          component={Link}
          to={DEMO_SESSION_PATH}
          sx={{ display: "block", px: 1.25, py: 1, textDecoration: "none", color: "inherit" }}
        >
          <Stack
            direction="row"
            sx={{ alignItems: "center", gap: 0.5, "@media (hover: none)": { pr: 4 } }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap title={title}>
              {title}
            </Typography>
          </Stack>
          {demo.data && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: "block", fontFamily: "monospace", mt: 0.25 }}
            >
              {demo.data.repository} · {demo.data.branch}
            </Typography>
          )}
          <Stack direction="row" sx={{ alignItems: "center", gap: 0.5, mt: 0.25 }}>
            <DemoChip />
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {t("demo_session.list_meta")}
            </Typography>
          </Stack>
        </Box>
        <Stack
          direction="row"
          className="session-actions"
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            zIndex: 1,
            borderRadius: 1,
            opacity: 0,
            transition: "opacity 120ms",
            bgcolor: "background.paper",
            "@media (hover: none)": { opacity: 1 },
          }}
        >
          <Tooltip title={t("demo_session.hide_tooltip")}>
            <IconButton
              size="small"
              aria-label={t("demo_session.hide")}
              disabled={hide.isPending}
              onClick={(e) => {
                e.preventDefault();
                hide.hide(() => {
                  if (selected) navigate("/app/sessions");
                });
              }}
            >
              <HideIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </ListItem>
  );
}
